import * as React from 'react';
import { Download } from 'lucide-react';

import { downloadFile } from '../../../lib/download';
import { cn } from '../../../lib/utils';
import { ImageLightbox } from './image-lightbox';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GalleryImage {
  /** Accessible alt text for this image. */
  alt: string;
  /** Filename suggested to the browser's download dialog. Defaults to the browser's own naming when omitted. */
  downloadFileName?: string;
  src: string;
  /** Smaller variant shown in the grid thumbnail; falls back to `src` when omitted. Lightbox/download always use `src`. */
  thumbnailSrc?: string;
}

export interface ImageGalleryProps {
  className?: string;
  /** Accessible label for the lightbox's close control. */
  closeAriaLabel: string;
  /** Resolves the accessible label for the download button of a given image index. */
  downloadAriaLabel: (index: number) => string;
  /** Optional `title` attribute shared by every download button (tooltip on hover). */
  downloadTitle?: string;
  images: GalleryImage[];
  /** Visually-hidden title passed through to the lightbox dialog for accessibility. */
  lightboxTitle: string;
  /** Accessible label for the lightbox's "next photo" control. */
  nextAriaLabel: string;
  /** Accessible label for the lightbox's "previous photo" control. */
  previousAriaLabel: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Generic responsive image grid: each thumbnail reveals a download button on
 * hover (top-right), and clicking a thumbnail opens a full-size lightbox
 * (embla Carousel) with prev/next navigation and download. No domain-specific
 * logic or copy lives here — all user-facing strings are passed in as props.
 */
export function ImageGallery({
  className,
  closeAriaLabel,
  downloadAriaLabel,
  downloadTitle,
  images,
  lightboxTitle,
  nextAriaLabel,
  previousAriaLabel,
}: ImageGalleryProps): React.JSX.Element {
  const [lightboxIndex, setLightboxIndex] = React.useState<number | null>(null);

  return (
    <>
      <div className={cn('grid w-full grid-cols-2 gap-2', className)}>
        {images.map((image, index) => (
          <div className="group relative" key={image.src}>
            <button
              aria-label={image.alt}
              className="block aspect-square w-full overflow-hidden rounded-md bg-muted focus:outline-none focus:ring-2 focus:ring-primary"
              onClick={(): void => setLightboxIndex(index)}
              type="button"
            >
              <img
                alt={image.alt}
                className="h-full w-full object-cover transition-transform hover:scale-[1.02]"
                loading="lazy"
                src={image.thumbnailSrc ?? image.src}
              />
            </button>
            <button
              aria-label={downloadAriaLabel(index)}
              className="absolute end-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center rounded-md bg-background/90 text-foreground opacity-0 shadow-sm transition-opacity hover:bg-background focus:opacity-100 group-hover:opacity-100"
              onClick={(): void => {
                void downloadFile(image.src, image.downloadFileName);
              }}
              title={downloadTitle}
              type="button"
            >
              <Download className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
      {lightboxIndex !== null && (
        <ImageLightbox
          closeAriaLabel={closeAriaLabel}
          downloadAriaLabel={downloadAriaLabel}
          downloadTitle={downloadTitle}
          images={images}
          initialIndex={lightboxIndex}
          nextAriaLabel={nextAriaLabel}
          onOpenChange={(open): void => {
            if (!open) setLightboxIndex(null);
          }}
          open
          previousAriaLabel={previousAriaLabel}
          title={lightboxTitle}
        />
      )}
    </>
  );
}
